import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import { api } from "../lib/api";
import MediaFrame from "../components/MediaFrame";
import Reveal from "../components/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [artists, setArtists] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 jm-container text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-jm-bone">Objet introuvable</h1>
        <Link to="/boutique" className="mt-6 inline-block text-jm-brass hover:text-jm-amber">← Retour à la boutique</Link>
      </main>
    );
  }
  if (!product) return <main className="min-h-screen pt-40 jm-container" data-testid="product-loading" />;

  const artist = artists.find((a) => a.id === product.artist);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", {
        ...form,
        subject: product.preorder ? `Précommande — ${product.name}` : `Boutique — ${product.name}`,
        kind: "boutique",
        message: form.message || `Je souhaite ${product.preorder ? "précommander" : "être informé·e de la disponibilité de"} : ${product.name} (${product.price} €).`,
      });
      toast.success(data.message);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="product-page" className="pt-28 sm:pt-36">
      <div className="jm-container pb-24 sm:pb-32">
        <Link to="/boutique" data-testid="product-back-link" className="inline-flex items-center gap-2 text-[13px] text-jm-smoke hover:text-jm-bone transition-colors mb-10">
          <ArrowLeft size={14} /> Boutique
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <Reveal>
            <div className="relative">
              <MediaFrame
                label={product.category}
                sublabel={artist?.name || "Joy Messenger"}
                accent={artist?.accent || "#C5A059"}
                image={product.image}
                className="aspect-square border border-jm-brass/15"
                testId="product-visual"
              />
              {product.badge && (
                <span className="absolute top-4 left-4 bg-jm-amber text-jm-ink font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5">
                  {product.badge}
                </span>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="jm-overline mb-4">{product.category}</p>
            <h1 className="font-serif font-light text-3xl sm:text-5xl text-jm-bone leading-[1.12]">{product.name}</h1>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-jm-brass">
              {artist ? artist.name : "Joy Messenger"}
            </p>
            <p className="mt-6 font-serif text-3xl text-jm-bone">{product.price} €</p>
            <p className="mt-6 text-jm-smoke leading-relaxed max-w-lg">{product.description}</p>
            <p className="mt-4 text-xs text-jm-mute leading-relaxed max-w-lg">
              Paiement en ligne bientôt disponible. En attendant, laissez-nous un message : nous vous réservons
              l'objet et vous recontactons pour finaliser.
            </p>

            <form onSubmit={submit} data-testid="product-inquiry-form" className="mt-10 border border-jm-brass/20 bg-jm-card/60 p-6 sm:p-8 space-y-5">
              <p className="font-serif text-xl text-jm-bone">
                {product.preorder ? "Précommander" : "Être informé·e / Commander"}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Votre nom"
                  data-testid="product-inquiry-name"
                  className="bg-transparent border border-jm-brass/25 focus:border-jm-amber px-4 py-3 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Votre email"
                  data-testid="product-inquiry-email"
                  className="bg-transparent border border-jm-brass/25 focus:border-jm-amber px-4 py-3 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors"
                />
              </div>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Message (quantité, taille, précisions…)"
                data-testid="product-inquiry-message"
                className="w-full bg-transparent border border-jm-brass/25 focus:border-jm-amber px-4 py-3 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="product-inquiry-submit"
                className="inline-flex items-center gap-2.5 bg-jm-amber text-jm-ink px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-jm-gold transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? "Envoi…" : "Envoyer la demande"}
                <Send size={14} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
